import dayjs from "dayjs"

import { convertFromTon, convertToTon, getTonUomId, isProductInTon, isTonUom, isUsUom } from "./conversion"

import {
  type AerosDateValue,
  DateSpecialValue,
} from "@/components/UiKit/AerosDatePicker/AerosDatePickerWithCustomActions"
import { DATE_FORMAT, UOM_RENDER_PRECISION } from "@/constants/formats"
import {
  type FreightTerms,
  type Product,
  type PurchaseOrderCalculatedTotals,
  type PurchaseOrderCalculatedTotalsItem,
  type PurchaseOrderPricingInput,
  type UnitOfMeasurement,
} from "@/graphql/codegen/graphql"
import { getLocationIdPerProductList } from "@/screens/Procurement/PurchaseOrder/hooks/utils/common"
import {
  type PricingOrderLine,
  RoundOffType,
  RoundPrecisionUnit,
} from "@/screens/Procurement/PurchaseOrder/ProductAdded/types/pricing"
import { type PurchaseOrderForm } from "@/screens/Procurement/PurchaseOrder/types"

export const getWeightPerPallet = (product: Product, uomId: string): number => {
  const { palletMetricWeight, palletUsWeight } = product

  const weightPerPallet = isUsUom(uomId) ? palletUsWeight : palletMetricWeight

  return weightPerPallet ?? 0
}

export interface OrderWeightTotals {
  usWeight: PurchaseOrderCalculatedTotalsItem | null | undefined
  usUom: UnitOfMeasurement | undefined
  metricWeight: PurchaseOrderCalculatedTotalsItem | null | undefined
  metricUom: UnitOfMeasurement | undefined
}

export const getDisplayableOrderWeightTotals = (
  totals: PurchaseOrderCalculatedTotals | null | undefined,
  units: UnitOfMeasurement[]
): OrderWeightTotals => {
  const isTons = totals ? isTonUom(totals.us.uomId) : false
  const usWeight = isTons ? totals?.usExtra : totals?.us
  const metricWeight = isTons ? totals?.metricExtra : totals?.metric
  const usUom = units.find((u) => u.id === usWeight?.uomId)
  const metricUom = units.find((u) => u.id === metricWeight?.uomId)

  return { usWeight, usUom, metricWeight, metricUom }
}

interface RoundingValues {
  lower: number
  upper: number
}

export const getRoundedPallets = (value: number): RoundingValues => {
  if (value % 1 === 0) {
    return { lower: value, upper: value } // Return the same value for whole numbers
  }

  const lower = Math.floor(value)
  const upper = Math.ceil(value)

  return { lower, upper }
}

export const getRoundedBags = ({
  palletsNumber,
  weightPerPallet,
  packageUsWeight,
}: {
  palletsNumber: number
  weightPerPallet: number
  packageUsWeight: number
}): RoundingValues => {
  const palettesTotalWeight = palletsNumber * weightPerPallet
  const minFullPalettesWeight = palettesTotalWeight - (palettesTotalWeight % packageUsWeight)

  const lower = minFullPalettesWeight / weightPerPallet
  const upper = (minFullPalettesWeight + packageUsWeight) / weightPerPallet

  return { lower, upper }
}

const getRoundedValues = ({
  product,
  uomId,
  roundPrecisionUnit,
  palletsNumber,
}: {
  product: Product
  uomId: string
  roundPrecisionUnit: RoundPrecisionUnit
  palletsNumber: number
}): {
  lower: number
  upper: number
} => {
  const packageUsWeight = product.packageUsWeight ?? 0
  const weightPerPallet = getWeightPerPallet(product, uomId)

  if (roundPrecisionUnit === RoundPrecisionUnit.Bag) {
    const palettesTotalWeight = palletsNumber * weightPerPallet
    const minFullPalettesWeight = palettesTotalWeight - (palettesTotalWeight % packageUsWeight)

    const lower = minFullPalettesWeight / weightPerPallet
    const upper = (minFullPalettesWeight + packageUsWeight) / weightPerPallet

    return { lower, upper }
  }

  if (roundPrecisionUnit === RoundPrecisionUnit.Pallet) {
    if (palletsNumber % 1 === 0) {
      return { lower: palletsNumber, upper: palletsNumber }
    }

    const lower = Math.floor(palletsNumber)
    const upper = Math.ceil(palletsNumber)

    return { lower, upper }
  }

  return { lower: 0, upper: 0 }
}

interface CalculateRoundingValuesParams {
  palletsNumber: number
  quantityInUom: number
  uomId: string
  product: Product
  roundPrecisionUnit: RoundPrecisionUnit
}

export const calculateRoundingValues = ({
  palletsNumber,
  quantityInUom,
  uomId,
  product,
  roundPrecisionUnit,
}: CalculateRoundingValuesParams): {
  lowerWeight: number
  upperWeight: number
  lowerNumber: number
  upperNumber: number
} => {
  const weightPerPallet = getWeightPerPallet(product, uomId)

  const { lower: lowerNumber, upper: upperNumber } = getRoundedValues({
    product,
    uomId,
    roundPrecisionUnit,
    palletsNumber,
  })

  let lowerWeight = weightPerPallet ? lowerNumber * weightPerPallet : quantityInUom
  let upperWeight = weightPerPallet ? upperNumber * weightPerPallet : quantityInUom

  if (isTonUom(uomId) && !isProductInTon(product)) {
    lowerWeight = convertToTon(lowerWeight, uomId)
    upperWeight = convertToTon(upperWeight, uomId)
  } else if (!isTonUom(uomId) && isProductInTon(product)) {
    const tonUomId = getTonUomId(uomId)

    lowerWeight = convertFromTon(lowerWeight, tonUomId)
    upperWeight = convertFromTon(upperWeight, tonUomId)
  }

  return {
    lowerWeight,
    upperWeight,
    lowerNumber,
    upperNumber,
  }
}

interface RoundOrderQuantityParams {
  line: PricingOrderLine
  roundOffType: RoundOffType | null
  upper: number
  upperPallets: number
  lower: number
  lowerPallets: number
}

export const hasFloatPallets = (numberOfPallets: number): boolean => (numberOfPallets ?? 0) % 1 !== 0

export const roundOrderQuantity = ({
  line,
  roundOffType,
  upper,
  upperPallets,
  lower,
  lowerPallets,
}: RoundOrderQuantityParams): PricingOrderLine => {
  if (roundOffType === RoundOffType.UP) {
    return {
      ...line,
      quantityInUom: upper,
      numberOfPallets: upperPallets,
      hasFloatPallets: hasFloatPallets(upperPallets),
    }
  }

  if (roundOffType === RoundOffType.DOWN) {
    return {
      ...line,
      quantityInUom: lower,
      numberOfPallets: lowerPallets,
      hasFloatPallets: hasFloatPallets(lowerPallets),
    }
  }

  return {
    ...line,
    hasFloatPallets: false,
  }
}

export const isBagsWholeNumber = (product: Product, quantityInUom: number, uomId: string): boolean => {
  const weight = isUsUom(uomId) ? product.packageUsWeight : product.packageMetricWeight

  if (!weight) {
    return false
  }

  return quantityInUom % weight === 0
}

export const parseNumberOfPallets = (numberOfPallets: number, isUserInput: boolean): string | number => {
  if (isUserInput && !numberOfPallets) {
    return ""
  }

  if (numberOfPallets) {
    return Number.parseFloat(numberOfPallets.toFixed(UOM_RENDER_PRECISION))
  }

  return 0
}

const createNextDay = () => dayjs().add(1, "day").format(DATE_FORMAT.ISO_18601)

// On PO create/update date must be sent as null when ASAP is selected.
// But in order to fetch actual pricing while PO is being edited, next day date is provided.
export const resolveSpecialDate = (
  date: AerosDateValue | undefined,
  sentinel: DateSpecialValue,
  preserveDate = false
): string | null => {
  if (date && date !== sentinel) {
    return date
  }
  return preserveDate && sentinel === DateSpecialValue.ASAP ? createNextDay() : null
}

const toOptional = (value: string | null | undefined): string | undefined => (value ? value : undefined)

export const mapCalculatedPricingParams = ({
  orderForm,
  product,
  quantity,
  requestedDeliveryDate,
  updatedUomId,
  incoterms,
  incotermsLocationId,
  freightTerms,
  warehouseAreaId,
}: {
  orderForm: PurchaseOrderForm
  requestedDeliveryDate?: AerosDateValue
  updatedUomId?: string
  product: Product | null
  quantity: number | null
  incoterms?: string | null
  incotermsLocationId?: string | null
  freightTerms?: FreightTerms | null
  warehouseAreaId?: string | null
}): PurchaseOrderPricingInput => {
  const sourceCompanyLocationAssociationId = getLocationIdPerProductList(
    orderForm.orderLines.map(({ product }) => product)
  )

  const date = requestedDeliveryDate !== undefined ? requestedDeliveryDate : orderForm.requestedDeliveryDate
  const resolvedIncoterms = incoterms !== undefined ? incoterms : orderForm.deliveryTerms
  const resolvedIncotermsLocationId =
    incotermsLocationId !== undefined ? incotermsLocationId : orderForm.deliveryLocationId
  const resolvedFreightTerms = freightTerms !== undefined ? freightTerms : orderForm.freightTerms
  const resolvedWarehouseAreaId = warehouseAreaId !== undefined ? warehouseAreaId : orderForm.warehouseAreaId

  return {
    orderLines: orderForm.orderLines.map(
      ({ productId, quantityInUom, uomId, productVersionId, isManualPriceSet, pricePerUom, priceUomId }) => ({
        productId,
        productVersionId,
        quantity: productId === product?.productId && quantity !== null ? quantity : quantityInUom,
        uomId: productId === product?.productId && !!updatedUomId ? updatedUomId : uomId,
        isManualPriceSet,
        pricePerUom: isManualPriceSet ? pricePerUom : undefined,
        priceUomId: isManualPriceSet ? priceUomId : undefined,
      })
    ),
    requestedDeliveryDate: resolveSpecialDate(date, DateSpecialValue.ASAP, true),
    sourceCompanyLocationAssociationId,
    incoterms: toOptional(resolvedIncoterms),
    incotermsLocationId: toOptional(resolvedIncotermsLocationId),
    freightTerms: resolvedFreightTerms ? resolvedFreightTerms : undefined,
    warehouseAreaId: toOptional(resolvedWarehouseAreaId),
  }
}
